import React from 'react';
import PIEText from '@src/shared/components/PIEText';
import styles from './DashboardDeliveryDataOrderTrackingChart.module.scss';
import PIETag, { ETagsColorType } from '@src/shared/components/PIETag';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import NoOrderOnOrderListImage from '@assets/no-order-orderlist.svg';
import {
  IconAlertCircleFilled,
  IconBag,
  IconCheckCircleFilled,
} from '@justeattakeaway/pie-icons-react';
import DashboardFAQList from '../../../DashboardFAQList';
import { useTranslation } from 'react-i18next';

interface componentsProps {
  totalOrdersCount: null | number;
  trackedOrdersPercentage: null | number;
}

const DashboardDeliveryDataOrderTrackingChart = ({
  totalOrdersCount = null,
  trackedOrdersPercentage = null,
}: componentsProps) => {
  const { t } = useTranslation();

  const data = {
    datasets: [
      {
        data: [100 - (trackedOrdersPercentage || 0), trackedOrdersPercentage || 0],
        backgroundColor: ['#FFD600', '#68CA77'],
        cutout: '75%',
        borderWidth: 0,
        borderRadius: 3,
        spacing: 3,
        rotation: 280,
      },
    ],
  };

  const renderStatusMessage = () => {
    if (trackedOrdersPercentage === null) return null;

    const getStatusConfig = () => {
      if (trackedOrdersPercentage >= 70) {
        return {
          icon: <IconCheckCircleFilled size="m" color="#68CA77" />,
          title: t('deliverydata.ORDER_SUMMARY_GREAT_WORK'),
          description: t('deliverydata.ORDER_SUMMARY_MOST_TRACKED'),
        };
      }
      if (trackedOrdersPercentage >= 50) {
        return {
          icon: null,
          title: t('deliverydata.ORDER_SUMMARY_KEEP_IT_UP'),
          description: t('deliverydata.ORDER_SUMMARY_SOME_TRACKED'),
        };
      }
      return {
        icon: <IconAlertCircleFilled size="m" color="#000000c2" />,
        title: t('deliverydata.ORDER_SUMMARY_HEADS_UP'),
        description: t('deliverydata.ORDER_SUMMARY_MOST_NOT_TRACKED'),
      };
    };

    const { icon, title, description } = getStatusConfig();

    return (
      <>
        {icon && <PIEComponentUtil marginBottom="var(--xds-spacing-a)">{icon}</PIEComponentUtil>}
        <PIEComponentUtil marginBottom="var(--xds-spacing-a)">
          <PIEText
            text={title}
            color="var(--xds-color-content-default)"
            size="var(--xds-font-size-14)"
            fontWeight="var(--xds-font-weight-bold)"
          />
        </PIEComponentUtil>
        <PIEText
          text={description}
          color="var(--xds-color-content-default)"
          size="var(--xds-font-size-14)"
          fontWeight="var(--xds-font-weight-regular)"
        />
      </>
    );
  };

  ChartJS.register(ArcElement, Tooltip, Legend);

  if (!trackedOrdersPercentage) {
    return (
      <>
        <img
          src={NoOrderOnOrderListImage}
          className={styles.DashboardDeliveryDataOrderTrackingChartPlaceholderImage}
        />
        <PIEComponentUtil marginTop="var(--xds-spacing-d)" padding="var(--xds-spacing-b)">
          <PIEText
            text={t('deliverydata.ORDER_EMPTY_STATE_TITLE')}
            color="var(--xds-color-content-default)"
            size="var(--xds-font-size-20)"
            fontWeight="var(--xds-font-weight-bold)"
            align="center"
          />
          <PIEComponentUtil marginTop="var(--xds-spacing-b)">
            <PIEText
              text={t('deliverydata.ORDER_EMPTY_STATE_DESCRIPTION')}
              color="var(--xds-color-content-default)"
              size="var(--xds-font-size-16)"
              fontWeight="var(--xds-font-weight-regular)"
              align="center"
            />
          </PIEComponentUtil>
        </PIEComponentUtil>
      </>
    );
  }

  return (
    <>
      <div className={styles.DashboardDeliveryDataOrderTrackingChartHeaderContainer}>
        <PIEText
          text={t('deliverydata.ORDER_SUMMARY_TITLE')}
          color="var(--xds-color-content-default)"
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-bold)"
        />
        <PIETag mode={ETagsColorType.Neutral}>{t('deliverydata.ORDER_SUMMARY_TAG')}</PIETag>
      </div>
      <div className={styles.DashboardDeliveryDataOrderTrackingChartSection}>
        <div className={styles.DashboardDeliveryDataOrderTrackingChartChartContainer}>
          <Doughnut options={{ responsive: true, maintainAspectRatio: false }} data={data} />
          <div className={styles.DashboardDeliveryDataOrderTrackingChartContent}>
            {renderStatusMessage()}
          </div>
        </div>
        <PIEComponentUtil
          paddingLeft="var(--xds-spacing-d)"
          paddingRight="var(--xds-spacing-d)"
          marginBottom="var(--xds-spacing-d)"
        >
          <table width="100%">
            <tbody>
              <tr>
                <td width={'80%'}>
                  <PIEText
                    text={
                      <>
                        <IconBag size="xs" /> {t('deliverydata.ORDERS_THIS_WEEK')}
                      </>
                    }
                    color="var(--xds-color-content-default)"
                    size="var(--xds-font-size-14)"
                    fontWeight="var(--xds-font-weight-regular)"
                  />
                </td>
                <td width={'80%'} align="right">
                  <PIEText
                    text={totalOrdersCount || 0}
                    color="var(--xds-color-content-default)"
                    size="var(--xds-font-size-16)"
                    fontWeight="var(--xds-font-weight-regular)"
                  />
                </td>
              </tr>
              <tr>
                <td width={'80%'}>
                  <PIEText
                    text={
                      <>
                        <span className={styles.DeliveredOrderArticlePoint}></span>
                        {t('deliverydata.TRACKED_ORDERS')}
                      </>
                    }
                    color="var(--xds-color-content-default)"
                    size="var(--xds-font-size-14)"
                    fontWeight="var(--xds-font-weight-regular)"
                  />
                </td>
                <td width={'20%'} align="right">
                  <PIEText
                    text={`${trackedOrdersPercentage || 0}%`}
                    color="var(--xds-color-content-default)"
                    size="var(--xds-font-size-16)"
                    fontWeight="var(--xds-font-weight-regular)"
                  />
                </td>
              </tr>
              <tr>
                <td width={'80%'}>
                  <PIEText
                    text={
                      <>
                        <span className={styles.NonDeliveredOrderArticlePoint}></span>
                        {t('deliverydata.NOT_TRACKED_ORDERS')}
                      </>
                    }
                    color="var(--xds-color-content-default)"
                    size="var(--xds-font-size-14)"
                    fontWeight="var(--xds-font-weight-regular)"
                  />
                </td>
                <td width={'20%'} align="right">
                  <PIEText
                    text={`${100 - (trackedOrdersPercentage || 0)}%`}
                    color="var(--xds-color-content-default)"
                    size="var(--xds-font-size-16)"
                    fontWeight="var(--xds-font-weight-regular)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </PIEComponentUtil>
      </div>
      <div className={styles.DashboardDeliveryDataOrderTrackingChartHeaderContainer}>
        <PIEText
          text={t('deliverydata.FAQ_SECTION_TITLE')}
          color="var(--xds-color-content-default)"
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-bold)"
        />
      </div>
      <div className={styles.DashboardDeliveryDataOrderTrackingChartSection}>
        <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
          <DashboardFAQList
            faqItems={[
              {
                question: t('deliverydata.FAQ_QUESTION_WHAT_ARE_TRACKED_ORDERS'),
                answer: t('deliverydata.FAQ_ANSWER_WHAT_ARE_TRACKED_ORDERS'),
              },
              {
                question: t('deliverydata.FAQ_QUESTION_WHAT_ARE_NON_TRACKED_ORDERS'),
                answer: t('deliverydata.FAQ_ANSWER_WHAT_ARE_NON_TRACKED_ORDERS'),
              },
              {
                question: t('deliverydata.FAQ_QUESTION_BENEFITS_OF_TRACKING'),
                answer: t('deliverydata.FAQ_ANSWER_BENEFITS_OF_TRACKING'),
              },
            ]}
          />
        </PIEComponentUtil>
      </div>
    </>
  );
};

export default DashboardDeliveryDataOrderTrackingChart;
